import React, { useEffect, useState, useCallback } from 'react';
import { Bot, Play, Pause, Clock, CheckCircle2, XCircle, RefreshCw, Plus, ChevronDown, ChevronUp, Zap, Calendar, Mail, Search, FileText, BarChart3, Briefcase, Users, Code, MessageSquare, TrendingUp } from 'lucide-react';

type Agent = {
  id: string;
  title: string;
  description: string | null;
  status: string;
  priority: string | null;
  cronExpression: string | null;
  cronTimezone: string | null;
  lastRunAt: string | null;
  lastRunStatus: string | null;
  lastRunSummary: string | null;
  nextRunAt: string | null;
  runCount: number;
  consecutiveFailures: number;
  createdAt: string;
  avatarUrl?: string | null;
  personaName?: string | null;
};

type RunHistoryEntry = {
  runAt: string;
  status: string;
  summary: string;
  model?: string;
  toolCalls?: number;
};

type AgentVisual = {
  icon: React.ReactNode;
  bgClass: string;
  textClass: string;
};

function getAgentVisual(title: string): AgentVisual {
  const lower = title.toLowerCase();
  if (lower.includes('lead') || lower.includes('prospect')) {
    return { icon: <Search className="w-4 h-4" />, bgClass: 'bg-emerald-500/15', textClass: 'text-emerald-400' };
  }
  if (lower.includes('email') || lower.includes('follow') || lower.includes('outreach')) {
    return { icon: <Mail className="w-4 h-4" />, bgClass: 'bg-blue-500/15', textClass: 'text-blue-400' };
  }
  if (lower.includes('report') || lower.includes('summary') || lower.includes('briefing')) {
    return { icon: <FileText className="w-4 h-4" />, bgClass: 'bg-amber-500/15', textClass: 'text-amber-400' };
  }
  if (lower.includes('analytic') || lower.includes('metric') || lower.includes('data')) {
    return { icon: <BarChart3 className="w-4 h-4" />, bgClass: 'bg-purple-500/15', textClass: 'text-purple-400' };
  }
  if (lower.includes('sales') || lower.includes('revenue') || lower.includes('pipeline')) {
    return { icon: <TrendingUp className="w-4 h-4" />, bgClass: 'bg-rose-500/15', textClass: 'text-rose-400' };
  }
  if (lower.includes('meeting') || lower.includes('team') || lower.includes('hr')) {
    return { icon: <Users className="w-4 h-4" />, bgClass: 'bg-indigo-500/15', textClass: 'text-indigo-400' };
  }
  if (lower.includes('code') || lower.includes('deploy') || lower.includes('build')) {
    return { icon: <Code className="w-4 h-4" />, bgClass: 'bg-cyan-500/15', textClass: 'text-cyan-400' };
  }
  if (lower.includes('chat') || lower.includes('message') || lower.includes('reply')) {
    return { icon: <MessageSquare className="w-4 h-4" />, bgClass: 'bg-pink-500/15', textClass: 'text-pink-400' };
  }
  return { icon: <Briefcase className="w-4 h-4" />, bgClass: 'bg-primary/15', textClass: 'text-primary' };
}

function AgentAvatar({ agent, isActive }: { agent: Agent; isActive: boolean }) {
  const visual = getAgentVisual(agent.title);
  if (agent.avatarUrl) {
    return (
      <div className="relative w-9 h-9 shrink-0">
        <img
          src={agent.avatarUrl}
          alt={agent.personaName || agent.title}
          className="w-9 h-9 rounded-lg object-cover"
        />
        {isActive && (
          <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-card" />
        )}
      </div>
    );
  }
  return (
    <div className="relative shrink-0">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${isActive ? visual.bgClass : 'bg-muted/30'} ${isActive ? visual.textClass : 'text-foreground/40'}`}>
        {visual.icon}
      </div>
      {isActive && (
        <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-card animate-pulse" />
      )}
    </div>
  );
}

function formatCron(cron: string | null): string {
  if (!cron) return 'No schedule';
  const map: Record<string, string> = {
    '0 8 * * 1-5': 'Weekdays 8 AM',
    '0 9 * * 1-5': 'Weekdays 9 AM',
    '0 10 * * 1-5': 'Weekdays 10 AM',
    '0 9 * * *': 'Daily 9 AM',
    '0 9 * * 1': 'Weekly Mon 9 AM',
    '0 9 1 * *': 'Monthly 1st 9 AM',
  };
  return map[cron] || cron;
}

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  if (diff < 60_000) return 'Just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    open: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
    active: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
    running: 'bg-blue-500/15 text-blue-400 border-blue-500/20',
    in_progress: 'bg-blue-500/15 text-blue-400 border-blue-500/20',
    completed: 'bg-foreground/10 text-foreground/50 border-foreground/10',
    cancelled: 'bg-red-500/15 text-red-400 border-red-500/20',
    paused: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
    failed: 'bg-red-500/15 text-red-400 border-red-500/20',
  };
  const label = status === 'open' ? 'Active' : status === 'in_progress' ? 'Running' : status.charAt(0).toUpperCase() + status.slice(1);
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${colors[status] || colors.completed}`}>
      {status === 'open' || status === 'active' ? <CheckCircle2 className="w-2.5 h-2.5" /> : status === 'cancelled' ? <XCircle className="w-2.5 h-2.5" /> : <Clock className="w-2.5 h-2.5" />}
      {label}
    </span>
  );
}

function AgentCard({ agent, onToggle, onRunNow }: { agent: Agent; onToggle: (id: string, active: boolean) => void; onRunNow: (id: string) => void }) {
  const [expanded, setExpanded] = useState(false);
  const isActive = agent.status === 'open' || agent.status === 'in_progress';

  return (
    <div className={`rounded-xl border transition-all duration-200 ${isActive ? 'border-border/60 bg-card/80' : 'border-border/30 bg-card/40 opacity-70'}`}>
      {/* Header */}
      <div className="flex items-start gap-3 p-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        {/* Avatar */}
        <AgentAvatar agent={agent} isActive={isActive} />

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-foreground truncate">{agent.title}</span>
          </div>
          {agent.personaName && (
            <span className="text-[10px] text-primary/70 font-medium">as {agent.personaName}</span>
          )}
          <div className="flex items-center gap-2 mt-0.5">
            <StatusBadge status={agent.status} />
            <span className="text-[10px] text-foreground/40 flex items-center gap-1">
              <Calendar className="w-2.5 h-2.5" />
              {formatCron(agent.cronExpression)}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 shrink-0">
          {isActive && (
            <button
              onClick={(e) => { e.stopPropagation(); onRunNow(agent.id); }}
              className="w-7 h-7 rounded-lg flex items-center justify-center text-foreground/40 hover:text-primary hover:bg-primary/10 transition-colors"
              title="Run now"
            >
              <Play className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onToggle(agent.id, isActive); }}
            className="w-7 h-7 rounded-lg flex items-center justify-center text-foreground/40 hover:text-foreground hover:bg-muted/30 transition-colors"
            title={isActive ? 'Pause agent' : 'Resume agent'}
          >
            {isActive ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
          </button>
          {expanded ? <ChevronUp className="w-3.5 h-3.5 text-foreground/30" /> : <ChevronDown className="w-3.5 h-3.5 text-foreground/30" />}
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="px-3 pb-3 border-t border-border/20 pt-2 space-y-2">
          {agent.description && (
            <p className="text-xs text-foreground/50 leading-relaxed">{agent.description}</p>
          )}

          {/* Stats row */}
          <div className="flex gap-3 text-[10px]">
            <div className="flex flex-col items-center px-3 py-1.5 rounded-lg bg-muted/20">
              <span className="font-bold text-foreground text-sm">{agent.runCount}</span>
              <span className="text-foreground/40">Runs</span>
            </div>
            <div className="flex flex-col items-center px-3 py-1.5 rounded-lg bg-muted/20">
              <span className="font-bold text-foreground text-sm">{agent.lastRunStatus === 'success' ? '✓' : agent.lastRunStatus === 'failed' ? '✗' : '—'}</span>
              <span className="text-foreground/40">Last</span>
            </div>
            <div className="flex flex-col items-center px-3 py-1.5 rounded-lg bg-muted/20">
              <span className="font-bold text-foreground text-sm">{timeAgo(agent.lastRunAt)}</span>
              <span className="text-foreground/40">Run at</span>
            </div>
            <div className="flex flex-col items-center px-3 py-1.5 rounded-lg bg-muted/20">
              <span className="font-bold text-foreground text-sm">{agent.consecutiveFailures}</span>
              <span className="text-foreground/40">Failures</span>
            </div>
          </div>

          {/* Last run summary */}
          {agent.lastRunSummary && (
            <div className="rounded-lg bg-muted/10 border border-border/20 p-2">
              <span className="text-[10px] font-semibold text-foreground/40 uppercase">Last Run Output</span>
              <p className="text-xs text-foreground/60 mt-1 line-clamp-3">{agent.lastRunSummary}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function TaskingBotAgentsPanel() {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAgents = useCallback(async () => {
    try {
      const res = await fetch('/_api/taskingbot/tasks?status=all', { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      const parsed = data?.json ? (typeof data.json === 'string' ? JSON.parse(data.json) : data.json) : data;
      const tasks = parsed?.tasks || parsed || [];

      // Filter to only cron/scheduled tasks = agents
      const agentTasks = (Array.isArray(tasks) ? tasks : []).filter(
        (t: any) => t.cronExpression && t.cronExpression.trim(),
      );

      setAgents(
        agentTasks.map((t: any) => {
          const meta = typeof t.metadata === 'string' ? JSON.parse(t.metadata || '{}') : t.metadata || {};
          const auto = meta.autonomous || {};
          const history: RunHistoryEntry[] = Array.isArray(meta.runHistory) ? meta.runHistory : [];
          let consecutiveFailures = 0;
          for (let i = history.length - 1; i >= 0; i--) {
            if (history[i].status === 'failed') consecutiveFailures++;
            else break;
          }
          return {
            id: t.id,
            title: t.title,
            description: t.description,
            status: t.status,
            priority: t.priority,
            cronExpression: t.cronExpression,
            cronTimezone: t.cronTimezone,
            lastRunAt: auto.lastRunAt || null,
            lastRunStatus: auto.lastRunStatus || null,
            lastRunSummary: auto.lastRunSummary || null,
            nextRunAt: null,
            runCount: auto.runCount || 0,
            consecutiveFailures,
            createdAt: t.createdAt,
            avatarUrl: meta.avatarUrl || meta.persona?.avatarUrl || null,
            personaName: meta.personaName || meta.persona?.name || null,
          };
        }),
      );
    } catch (err) {
      console.error('[Agents] Failed to fetch:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAgents();
    const interval = setInterval(fetchAgents, 30_000);
    return () => clearInterval(interval);
  }, [fetchAgents]);

  const handleToggle = async (id: string, currentlyActive: boolean) => {
    try {
      await fetch('/_api/taskingbot/tasks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action: 'update',
          taskId: id,
          status: currentlyActive ? 'cancelled' : 'open',
        }),
      });
      fetchAgents();
    } catch (err) {
      console.error('[Agents] Toggle failed:', err);
    }
  };

  const handleRunNow = async (id: string) => {
    try {
      await fetch('/_api/taskingbot/tasks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action: 'update',
          taskId: id,
          metadata: { autonomous: { executionStatus: 'queued' } },
        }),
      });
      fetchAgents();
    } catch (err) {
      console.error('[Agents] Run now failed:', err);
    }
  };

  const activeAgents = agents.filter((a) => a.status === 'open' || a.status === 'in_progress');
  const pausedAgents = agents.filter((a) => a.status === 'cancelled' || a.status === 'completed');

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/40 bg-background/50 shrink-0">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-primary" />
          <span className="text-sm font-bold text-foreground">Agents</span>
          <span className="text-[10px] text-foreground/40 bg-muted/30 px-1.5 py-0.5 rounded-full">{activeAgents.length} active</span>
        </div>
        <button
          onClick={fetchAgents}
          className="w-7 h-7 rounded-lg flex items-center justify-center text-foreground/40 hover:text-foreground hover:bg-muted/30 transition-colors"
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Agent list */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {loading ? (
          <div className="flex items-center justify-center h-32 text-foreground/30 text-sm">Loading agents...</div>
        ) : agents.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-foreground/30 gap-2 text-center px-4">
            <Bot className="w-10 h-10" />
            <span className="text-sm font-semibold text-foreground/50">No agents yet</span>
            <span className="text-xs text-foreground/30 leading-relaxed">
              Any scheduled task becomes an agent. Create one in the Tasks panel with a recurring schedule, or ask the bot:<br />
              <span className="text-primary/60 italic mt-1 inline-block">"Create a daily lead research agent"</span>
            </span>
          </div>
        ) : (
          <>
            {activeAgents.length > 0 && (
              <>
                <div className="text-[10px] font-semibold text-foreground/30 uppercase tracking-wider px-1">Active</div>
                {activeAgents.map((agent) => (
                  <AgentCard key={agent.id} agent={agent} onToggle={handleToggle} onRunNow={handleRunNow} />
                ))}
              </>
            )}
            {pausedAgents.length > 0 && (
              <>
                <div className="text-[10px] font-semibold text-foreground/30 uppercase tracking-wider px-1 mt-3">Paused</div>
                {pausedAgents.map((agent) => (
                  <AgentCard key={agent.id} agent={agent} onToggle={handleToggle} onRunNow={handleRunNow} />
                ))}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
